import type { ChatMessage } from '../components/GlobalChatSystem/types';
import { ts } from '../repositories/firestoreUtils';

export const MAX_THREAD_MESSAGES = 100;

export interface ChatThreadMessageDoc extends Record<string, unknown> {
  timestamp: string;
}

export interface ChatThreadMessagesDoc {
  threadId: string;
  instanceId: string;
  messages: ChatThreadMessageDoc[];
  updatedAtTs: ReturnType<typeof ts.serverNow>;
  schemaVersion: 1;
}

function sanitizeForFirestore(value: unknown): unknown {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value.toISOString() : undefined;
  }

  const type = typeof value;
  if (type === 'string' || type === 'number' || type === 'boolean') return value;

  if (Array.isArray(value)) {
    const out: unknown[] = [];
    for (const item of value) {
      const cleaned = sanitizeForFirestore(item);
      if (cleaned !== undefined) out.push(cleaned);
    }
    return out;
  }

  if (type === 'object') {
    const out: Record<string, unknown> = {};
    const obj = value as Record<string, unknown>;
    for (const key of Object.keys(obj)) {
      const cleaned = sanitizeForFirestore(obj[key]);
      if (cleaned !== undefined) out[key] = cleaned;
    }
    return out;
  }

  return undefined;
}

function toTimestampIso(value: unknown): string {
  if (typeof value === 'string') {
    const parsed = new Date(value);
    if (Number.isFinite(parsed.getTime())) return parsed.toISOString();
  }
  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return value.toISOString();
  }
  return new Date(0).toISOString();
}

export function toMessageDoc(message: ChatMessage): ChatThreadMessageDoc {
  const sanitized = (sanitizeForFirestore(message) || {}) as Record<string, unknown>;
  return {
    ...sanitized,
    timestamp: toTimestampIso(sanitized.timestamp ?? message.timestamp),
  };
}

/** Revive a single stored message doc back into a ChatMessage (Date timestamp). */
export function fromMessageDoc(message: Record<string, unknown>): ChatMessage {
  const revived = (reviveTimestampFields(message) || {}) as Record<string, unknown>;
  const timestamp = revived.timestamp;
  return {
    ...(revived as unknown as ChatMessage),
    timestamp: timestamp instanceof Date ? timestamp : new Date(0),
  };
}

function reviveTimestampFields(value: unknown, key?: string): unknown {
  if (value === undefined || value === null) return value;

  if (key === 'timestamp' && typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime()) ? parsed : value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => reviveTimestampFields(item));
  }

  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    const obj = value as Record<string, unknown>;
    for (const childKey of Object.keys(obj)) {
      const child = reviveTimestampFields(obj[childKey], childKey);
      if (child !== undefined) out[childKey] = child;
    }
    return out;
  }

  return value;
}

// ── Firestore 1MiB doc-limit guard (2026-08-20 attachment-durability round) ──
// One oversized inline dataUrl used to make EVERY subsequent upsert of the
// whole-thread array doc fail — and the failures were swallowed, so the
// thread's server transcript silently froze at the message before the
// attachment (the "attachment disappeared over time" class: any server-
// sourced reload showed the pre-attachment thread). Inline dataUrls are the
// only field that reaches this scale, so the guard strips THEM — the record
// keeps name/type/size and every durable locator (storagePath/storageUrl/
// gcsPath), the tile still renders, the resolver path still downloads, and
// the local lane keeps its own dataUrl copy.
export const THREAD_DOC_BYTE_BUDGET = 900_000;
export const PER_MESSAGE_INLINE_BYTE_BUDGET = 250_000;

export function utf8Bytes(value: unknown): number {
  let json = '';
  try {
    json = JSON.stringify(value) ?? '';
  } catch {
    return 0;
  }
  // TextEncoder in browsers; Buffer under jest/node (jsdom exposes neither
  // guarantee). String length is the last resort — it undercounts multibyte
  // text, which the budgets' headroom absorbs.
  try {
    if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(json).length;
  } catch { /* fall through */ }
  try {
    if (typeof Buffer !== 'undefined') return Buffer.byteLength(json, 'utf8');
  } catch { /* fall through */ }
  return json.length;
}

/**
 * Per-field body caps (INCIDENT-20260822, the vanished translation).
 *
 * A finalized translation message carries the full document THREE times —
 * text (bodyContent), html (coverage + formatted body), and
 * workflowOutput.content (bodyContent again) — so one large translation blows
 * Firestore's ~1MiB doc limit no matter how many attachment dataUrls the
 * guard below strips. The setDoc was rejected with invalid-argument (not in
 * the transient-retry list, and swallowed — see firestoreUtils), and because
 * this lane writes the WHOLE message array as one doc, EVERY later save of
 * the thread failed too: the server transcript froze at the last
 * pre-finalize write. Sign-out purged the local copy; sign-in restored the
 * frozen partial server copy. The user watched a finished translation
 * vanish.
 *
 * The caps mirror conversationHistoryFirestore's (same numbers, same
 * truncation-notice pattern) — one uniform policy for what a persisted
 * message body may weigh, per the owner's platform-thread-uniformity
 * directive. The full document still reaches the user via the PDF/DOCX
 * deliverables (durable Storage locators) and the local workflow output
 * store.
 */
const MAX_PERSISTED_MESSAGE_TEXT_CHARS = 20_000;
const MAX_PERSISTED_MESSAGE_HTML_CHARS = 30_000;
const MAX_PERSISTED_WORKFLOW_CONTENT_CHARS = 20_000;
const PERSIST_TRUNCATION_NOTICE = '\n\n[... truncated for storage — the attached files carry the complete document ...]';

function truncateBody(value: unknown, maxChars: number): unknown {
  if (typeof value !== 'string' || value.length <= maxChars) return value;
  const keep = Math.max(0, maxChars - PERSIST_TRUNCATION_NOTICE.length);
  return value.slice(0, keep) + PERSIST_TRUNCATION_NOTICE;
}

/** Bound every body field a message doc may persist. Exported so the
 *  per-message lane (toMessageDocV2) applies the IDENTICAL policy. */
export function capMessageBodyFields(doc: ChatThreadMessageDoc): ChatThreadMessageDoc {
  const rec = doc as Record<string, unknown>;
  const text = truncateBody(rec.text, MAX_PERSISTED_MESSAGE_TEXT_CHARS);
  const html = truncateBody(rec.html, MAX_PERSISTED_MESSAGE_HTML_CHARS);
  const wf = rec.workflowOutput as Record<string, unknown> | undefined;
  const wfContent = wf ? truncateBody(wf.content, MAX_PERSISTED_WORKFLOW_CONTENT_CHARS) : undefined;
  const changed = text !== rec.text || html !== rec.html || (wf && wfContent !== wf.content);
  if (!changed) return doc;
  return {
    ...doc,
    ...(text !== rec.text ? { text } : {}),
    ...(html !== rec.html ? { html } : {}),
    ...(wf && wfContent !== wf.content
      ? { workflowOutput: { ...wf, content: wfContent } }
      : {}),
    // Diagnostic breadcrumb: capped here, not born short.
    bodyTruncatedForFirestore: true,
  } as ChatThreadMessageDoc;
}

export function hasInlineDataUrl(doc: ChatThreadMessageDoc): boolean {
  const list = (doc as Record<string, unknown>).attachmentsSerialized;
  return Array.isArray(list)
    && list.some((a) => typeof (a as Record<string, unknown>)?.dataUrl === 'string'
      && ((a as Record<string, unknown>).dataUrl as string).length > 0);
}

export function stripInlineDataUrls(doc: ChatThreadMessageDoc): ChatThreadMessageDoc {
  const list = (doc as Record<string, unknown>).attachmentsSerialized;
  if (!Array.isArray(list)) return doc;
  return {
    ...doc,
    attachmentsSerialized: list.map((raw) => {
      const att = { ...(raw as Record<string, unknown>) };
      if (typeof att.dataUrl === 'string' && att.dataUrl) {
        delete att.dataUrl;
        // Diagnostic breadcrumb: the record was capped here, not born bare.
        att.inlineDataOmitted = true;
      }
      return att;
    }),
  };
}

export function toChatThreadMessagesDoc(
  threadId: string,
  instanceId: string,
  messages: ChatMessage[],
): ChatThreadMessagesDoc {
  let docs = messages.slice(-MAX_THREAD_MESSAGES).map(toMessageDoc);

  // Body cap FIRST: no message body may exceed the uniform per-field budget
  // (INCIDENT-20260822 — see capMessageBodyFields).
  docs = docs.map(capMessageBodyFields);

  // Per-message cap: a single message may never carry more inline payload
  // than the budget — past it, ALL of its dataUrls strip (durable locators
  // and metadata stay).
  docs = docs.map((doc) => (
    hasInlineDataUrl(doc) && utf8Bytes(doc) > PER_MESSAGE_INLINE_BYTE_BUDGET
      ? stripInlineDataUrls(doc)
      : doc
  ));

  // Whole-doc cap: if the array doc still exceeds the budget, strip inline
  // payloads OLDEST-FIRST until it fits — newest attachments keep their
  // inline copy the longest.
  let totalBytes = utf8Bytes(docs);
  if (totalBytes > THREAD_DOC_BYTE_BUDGET) {
    let strippedCount = 0;
    for (let i = 0; i < docs.length && totalBytes > THREAD_DOC_BYTE_BUDGET; i++) {
      if (!hasInlineDataUrl(docs[i])) continue;
      docs[i] = stripInlineDataUrls(docs[i]);
      strippedCount += 1;
      totalBytes = utf8Bytes(docs);
    }
    if (strippedCount > 0) {
      // eslint-disable-next-line no-console
      console.warn('[chatThreadMessages] thread doc over Firestore budget — stripped inline attachment payloads from oldest messages', {
        threadId,
        strippedCount,
        totalBytes,
      });
    }
  }

  // LAST RESORT (INCIDENT-20260822): with bodies capped and dataUrls
  // stripped, a very long thread of capped-size messages could still exceed
  // the doc budget. Drop html from the OLDEST messages until it fits — html
  // is derivable from text at render, and a shorter old message beats a
  // thread whose every future save Firestore rejects.
  totalBytes = utf8Bytes(docs);
  if (totalBytes > THREAD_DOC_BYTE_BUDGET) {
    let htmlDropped = 0;
    for (let i = 0; i < docs.length && totalBytes > THREAD_DOC_BYTE_BUDGET; i++) {
      const rec = docs[i] as Record<string, unknown>;
      if (typeof rec.html !== 'string' || !rec.html) continue;
      docs[i] = { ...docs[i], html: '', htmlDroppedForFirestore: true } as ChatThreadMessageDoc;
      htmlDropped += 1;
      totalBytes = utf8Bytes(docs);
    }
    if (htmlDropped > 0) {
      // eslint-disable-next-line no-console
      console.warn('[chatThreadMessages] thread doc STILL over budget — dropped html from oldest messages', {
        threadId,
        htmlDropped,
        totalBytes,
      });
    }
  }

  // CARD-HTML BACKSTOP (adversarial review 2026-08-29): action cards now
  // carry FULL draft bodies in workflowActionButtonsHtml (one-tile
  // directive), a field none of the loops above touch — a few large tiles in
  // the 100-message window could still push the array doc past the budget
  // and freeze every future save (the INCIDENT-20260822 class). Drop the
  // OLDEST messages' large card html whole — never mid-truncate markup (a
  // clipped card ships broken buttons) — with a breadcrumb. The live/local
  // copy keeps rendering; only the oldest server copies degrade. Small
  // button rows (under the floor) stay: they are cheap and functional.
  //
  // `payload` is deliberately UNTOUCHED (P0 thread-durability, 2026-09-02):
  // it is what the connect-tile rebuild re-projects the card from at the
  // SET_MESSAGES boundary (connectTileRehydrate.ts), and the cross-store
  // merge now scores card html as body, so the intact per-doc copy wins the
  // dual read. The blank-plus-flag output stays deliberately minimal — a
  // placeholder shell would only add bytes to a doc that is already over
  // budget. A rebuilt copy that is re-persisted here re-blanks
  // deterministically while the doc stays over budget (no growth).
  const CARD_HTML_DROP_FLOOR_CHARS = 2_000;
  totalBytes = utf8Bytes(docs);
  if (totalBytes > THREAD_DOC_BYTE_BUDGET) {
    let cardHtmlDropped = 0;
    for (let i = 0; i < docs.length && totalBytes > THREAD_DOC_BYTE_BUDGET; i++) {
      const rec = docs[i] as Record<string, unknown>;
      if (typeof rec.workflowActionButtonsHtml !== 'string'
        || rec.workflowActionButtonsHtml.length <= CARD_HTML_DROP_FLOOR_CHARS) continue;
      docs[i] = {
        ...docs[i],
        workflowActionButtonsHtml: '',
        cardHtmlDroppedForFirestore: true,
      } as ChatThreadMessageDoc;
      cardHtmlDropped += 1;
      totalBytes = utf8Bytes(docs);
    }
    if (cardHtmlDropped > 0) {
      // eslint-disable-next-line no-console
      console.warn('[chatThreadMessages] thread doc STILL over budget — dropped card html from oldest messages', {
        threadId,
        cardHtmlDropped,
        totalBytes,
      });
    }
  }

  return {
    threadId,
    instanceId,
    messages: docs,
    updatedAtTs: ts.serverNow(),
    schemaVersion: 1,
  };
}

export function fromChatThreadMessagesDoc(doc: ChatThreadMessagesDoc): ChatMessage[] {
  const rawMessages = Array.isArray(doc.messages) ? doc.messages : [];

  return rawMessages.map((message) => {
    const revived = (reviveTimestampFields(message) || {}) as Record<string, unknown>;
    const timestamp = revived.timestamp;
    return {
      ...(revived as unknown as ChatMessage),
      timestamp: timestamp instanceof Date ? timestamp : new Date(0),
    };
  });
}
